import {
  XLSX_MAX_ROWS,
  XLSX_STATIC_PARTS,
  exportColumns,
  xlsxSheetEnd,
  xlsxSheetRows,
  xlsxSheetStart,
  type ExportWriter,
} from './auditExport';

/*
 * The one piece of the export that needs fflate, kept apart: fflate is a
 * dependency of frontend/app only, and CI's test job installs the root's, so
 * a tested module importing it fails to type-check (the XML parts it zips are
 * tested in auditExport.test.ts).
 */

/** The XLSX writer, deflating as rows arrive (fflate, loaded on first use). */
export async function createXlsxWriter(timeZone: string): Promise<ExportWriter> {
  const { Zip, ZipDeflate, strToU8 } = await import('fflate');
  const columns = exportColumns(timeZone);
  const chunks: Uint8Array[] = [];
  let failure: Error | null = null;
  let done: () => void = () => {};
  const finished = new Promise<void>((resolve) => (done = resolve));

  const zip = new Zip((err, data, final) => {
    if (err) failure = err;
    else chunks.push(data);
    if (err || final) done();
  });
  for (const [path, xml] of Object.entries(XLSX_STATIC_PARTS)) {
    const file = new ZipDeflate(path, { level: 6 });
    zip.add(file);
    file.push(strToU8(xml), true);
  }
  const sheet = new ZipDeflate('xl/worksheets/sheet1.xml', { level: 6 });
  zip.add(sheet);
  sheet.push(strToU8(xlsxSheetStart(columns)));
  let nextRow = 2;

  return {
    addRows(entries) {
      if (nextRow - 1 + entries.length > XLSX_MAX_ROWS - 1) {
        throw new Error(`Excel sheets hold at most ${(XLSX_MAX_ROWS - 1).toLocaleString('en-US')} rows: export as CSV instead.`);
      }
      sheet.push(strToU8(xlsxSheetRows(columns, entries, nextRow)));
      nextRow += entries.length;
    },
    async finish() {
      sheet.push(strToU8(xlsxSheetEnd(columns, nextRow - 1)), true);
      zip.end();
      await finished;
      if (failure) throw failure;
      return new Blob(chunks as BlobPart[], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    },
  };
}
